// src/DebtorList.js
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../layouts/MainLayout';
import DebtorRow from '../../components/DebtorRow';
import Pagination from '../../components/Pagination';
import { baseAPI } from '../../config';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import userStore from '../../store/userStore';

// Spinner animado para la carga
const Spinner = () => (
  <div className='flex flex-col items-center justify-center py-12'>
    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
    <p className='mt-4 text-sm text-slate-600'>Cargando deudores...</p>
  </div>
);

const DebtorList = () => {
  const [totalDebtors, setTotalDebtors] = useState(0);
  const [filteredDebtors, setFilteredDebtors] = useState([]);
  const [loadingDebtors, setLoadingDebtors] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 24;
  const { user } = userStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;

  // Se lanza una petición por tecla: solo cuenta la respuesta de la última, para
  // que una lenta de «6051» no pise a la de «605184968».
  const ultimaPeticion = useRef(0);

  const fetchDebtorsWithExpedients = async (page = 1, search = '') => {
    const peticion = ++ultimaPeticion.current;
    setLoadingDebtors(true);
    setError(null);
    try {
      const userToken = localStorage.getItem('token');
      if (!userToken) {
        setError('No se encontró el token de autenticación.');
        return;
      }

      const params = {
        page,
        limit: itemsPerPage,
        search: search.trim(),
      };

      const response = await axios.get(`${baseAPI}/debtors/debtors-with-expedients`, {
        params,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });

      if (peticion !== ultimaPeticion.current) return;
      setTotalDebtors(response.data.total);
      setTotalPages(response.data.totalPages);
      setFilteredDebtors(response.data.debtors);
    } catch (error) {
      if (peticion !== ultimaPeticion.current) return;
      console.error('Error fetching debtors:', error);
      setError('No se pudieron cargar los deudores. Por favor, inténtalo de nuevo.');
    } finally {
      if (peticion === ultimaPeticion.current) setLoadingDebtors(false);
    }
  };

  useEffect(() => {
    fetchDebtorsWithExpedients(currentPage, searchTerm);
  }, [currentPage, searchTerm]);

  const updateUrlParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleSearch = e => {
    const value = e.target.value;
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleSearchSubmit = e => {
    e.preventDefault();
  };

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateUrlParam('page', newPage.toString());
    }
  };

  // Función para la fusión de expedientes que llama al backend
  const onMergeExpedients = async (mainExpedient, mergeData) => {
    try {
      const response = await axios.post(
        `${baseAPI}/expedients/merge-expedients`,
        {
          mainExpedient,
          expedientsToCancel: mergeData.expedientsToCancel,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      console.log('Respuesta del backend:', response.data);
    } catch (error) {
      console.error('Error al fusionar expedientes:', error);
      throw error;
    }
  };

  return (
    <MainLayout>
      <div className='max-w-6xl mx-auto'>
        {/* Page header */}
        <div className='sm:flex sm:items-center sm:justify-between mb-8'>
          <div className='mb-4 sm:mb-0'>
            <div className='flex items-center'>
              <div className='mr-4'>
                <UserGroupIcon className='w-7 h-7 text-blue-600' />
              </div>
              <div>
                <h1 className='text-2xl font-semibold text-slate-900'>Listado de Deudores</h1>
                <p className='mt-1.5 text-sm text-slate-600'>Gestiona y visualiza todos los deudores registrados</p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className='bg-white px-4 py-3 rounded-lg border border-slate-200 flex items-center gap-4'>
            <div className='text-center px-4 border-r border-slate-200'>
              <div className='text-2xl font-bold text-slate-800'>{totalDebtors}</div>
              <div className='text-xs text-slate-500'>Total Deudores</div>
            </div>
            <div className='text-center px-4'>
              <div className='text-2xl font-bold text-slate-800'>{currentPage}</div>
              <div className='text-xs text-slate-500'>Página</div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearchSubmit} className='relative mb-8'>
          <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
            <MagnifyingGlassIcon className='h-5 w-5 text-slate-400' />
          </div>
          <input
            type='text'
            value={searchTerm}
            onChange={handleSearch}
            placeholder='Buscar por NIF, nombre o teléfono de deudor...'
            className='w-full pl-11 pr-4 py-3 text-sm bg-white border border-slate-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150'
          />
          <button
            type='submit'
            className='absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-blue-600 transition duration-150'
          >
            <MagnifyingGlassIcon className='h-5 w-5' />
          </button>
        </form>

        {/* Content */}
        {error ? (
          <div className='bg-amber-50 border border-amber-200 rounded-lg p-6 text-center'>
            <ExclamationTriangleIcon className='h-12 w-12 text-amber-500 mx-auto mb-4' />
            <p className='text-amber-800 mb-4'>{error}</p>
            <button
              onClick={() => fetchDebtorsWithExpedients(currentPage, searchTerm)}
              className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-150'
            >
              <ArrowPathIcon className='h-4 w-4 mr-2' />
              Reintentar
            </button>
          </div>
        ) : loadingDebtors ? (
          <Spinner />
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {filteredDebtors.length > 0 ? (
              filteredDebtors.map(debtor => (
                <DebtorRow
                  key={debtor._id}
                  debtor={debtor}
                  onMergeExpedients={onMergeExpedients}
                  userRole={user?.role}
                />
              ))
            ) : (
              <div className='col-span-full bg-white rounded-lg border border-slate-200 p-12 text-center'>
                <MagnifyingGlassIcon className='h-12 w-12 text-slate-400 mx-auto mb-4' />
                <h3 className='text-lg font-medium text-slate-900 mb-2'>No se encontraron deudores</h3>
                <p className='text-sm text-slate-500'>
                  No hay deudores que coincidan con tu búsqueda. Intenta con otros términos.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='mt-8'>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DebtorList;
